import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import type { AgentCommandStatus } from '@/lib/api';

// Shared staged-startup vocabulary + widgets, matching the Event Mode panel so the
// Ad Hoc tab reads identically. Stages are what the agent reports via the command
// queue (/commands/:id/progress) as a start walks to go-live.
export const STAGE_ORDER = ['queued', 'validating', 'youtube', 'obs', 'starting', 'waiting_live', 'live'] as const;

export const STAGE_LABEL: Record<string, string> = {
  queued: 'Queued', validating: 'Validating', youtube: 'Creating broadcast',
  obs: 'Starting OBS', starting: 'Going on air', waiting_live: 'Waiting for live signal', live: 'Live',
  streaming: 'Live',
};

// Friendlier past-tense labels for the retained checklist.
export const STEP_LABEL: Record<string, string> = {
  queued: 'Queued', validating: 'Validated', youtube: 'Broadcast created',
  obs: 'OBS started', starting: 'Going on air', waiting_live: 'Waiting for live signal', live: 'Live on YouTube',
  discord: 'Confirmation sent to Discord',
};

function timeOnly(iso: string | null | undefined, tz?: string): string {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', ...(tz ? { timeZone: tz } : {}) }); }
  catch { return ''; }
}

// Inline "current stage" pill while a start command is in flight (or a red Failed pill).
export function StageBadge({ cmd }: { cmd: AgentCommandStatus | undefined }) {
  if (!cmd) return null;
  if (cmd.status === 'pending' || cmd.status === 'delivered' || cmd.status === 'running') {
    const idx = cmd.stage ? STAGE_ORDER.indexOf(cmd.stage as typeof STAGE_ORDER[number]) : -1;
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
        <Loader2 className="h-3 w-3 animate-spin" />
        {STAGE_LABEL[cmd.stage ?? 'queued'] ?? 'Starting'}
        {idx >= 0 && <span className="text-blue-400">({idx + 1}/{STAGE_ORDER.length})</span>}
      </span>
    );
  }
  if (cmd.status === 'failed') {
    return (
      <span title={cmd.message ?? 'Start failed'} className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300">
        <AlertCircle className="h-3 w-3" /> Failed
      </span>
    );
  }
  return null;
}

// Timestamped checklist built from a command's stageHistory (+ a red failing step).
export function StageChecklist({ cmd, tz }: { cmd: AgentCommandStatus | undefined; tz?: string }) {
  const history = cmd?.stageHistory ?? [];
  const steps: Array<{ label: string; at?: string | null; ok: boolean }> = [];
  for (const h of history) steps.push({ label: STEP_LABEL[h.stage] ?? h.stage, at: h.at, ok: true });
  if (cmd?.status === 'failed') steps.push({ label: cmd.message || 'Start failed', at: cmd.completedAt, ok: false });
  if (steps.length === 0) {
    return <p className="px-3 py-2 text-xs text-gray-400">No activity yet — steps will appear here as the stream starts.</p>;
  }
  return (
    <ul className="space-y-1 px-3 py-2">
      {steps.map((s, i) => (
        <li key={i} className="flex items-center gap-2 text-xs">
          {s.ok
            ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600 dark:text-green-400" />
            : <AlertCircle className="h-3.5 w-3.5 shrink-0 text-red-600 dark:text-red-400" />}
          <span className={s.ok ? 'text-gray-700 dark:text-gray-300' : 'text-red-600 dark:text-red-400'}>{s.label}</span>
          {s.at && <span className="ml-auto tabular-nums text-gray-400">{timeOnly(s.at, tz)}</span>}
        </li>
      ))}
    </ul>
  );
}
